import BaseTypesenseSearch, { ON_RENDER_INSTANTSEARCH } from '../typesense-search';
import { ConfigureWidgetParams } from 'instantsearch.js/es/widgets/configure/configure';
import SearchSection from './search-section';

interface ISection {
  indexname: string;
  collectionName: string;
  nbHits: number;
  configureParams?: ConfigureWidgetParams;
  configureParamsExtend?: ConfigureWidgetParams;
  searchParams?: { [key: string]: { [key: string]: string | number } };
  loaded?: boolean;
}

export default class GlobalSearch extends BaseTypesenseSearch {
  private sections: ISection[] | undefined = [];
  private totalResultsContainerEl: HTMLDivElement | undefined = undefined;
  private query = '';
  private activeSection = 'tous';
  private isQuerySent = false;
  private searchEventUserId = '';

  constructor() {
    super();
  }

  getSections(indexList?: string): ISection[] | undefined {
    try {
      const sections = JSON.parse(indexList || '{}');
      if (sections?.length) {
        return sections.map(section => ({
          indexname: section.indexname,
          collectionName: section.collection_name,
          nbHits: 0,
          configureParams: section.configure_params,
          configureParamsExtend: section.configure_params_extend,
          searchParams: section.search_params,
          loaded: false,
        }) as ISection);
      }
    } catch (e: unknown) {
      console.log('Error while parsing index list : ', (e as Error).message);
    }

    return undefined;
  }

  getTotalRecords() {
    return this.sections?.reduce((accu, item) => accu + item.nbHits, 0) ?? 0;
  }

  updateTotalTitle(stat: { indexname: string, results: { nbHits: number } }) {
    // Set
    const section = this.sections?.find(section => section.indexname === stat.indexname);
    if (section) {
      section.nbHits = stat.results.nbHits;
    }
    const totalRecords = this.getTotalRecords();
    let resultText = '';

    if (this.totalResultsContainerEl) {
      if (this.query) {
        if (totalRecords) {
          resultText = `<h3>Résultats pour “${this.query}” <span class="nb-hits">(${totalRecords})</span></h3>`;
        } else {
          resultText = `<h3>Aucun résultat pour “${this.query}”`;
        }
        this.totalResultsContainerEl.innerHTML = resultText;
      }
    }
  }

  changeSectionSearchConfigure(collectionName: string) {
    const allSections = this.querySelectorAll('.column-right search-section');
    allSections.forEach((sectionEl) => {
      const searchBlock = sectionEl.querySelector('.search-block');
      if (searchBlock) {
        const { indexname } = (sectionEl as SearchSection).dataset;
        const config = this.sections?.find(section => section.indexname === indexname);

        if (config) {
          const configToApply: ConfigureWidgetParams | undefined = collectionName !== indexname ? config.configureParams : config.configureParamsExtend;

          searchBlock.setAttribute('data-configure-params', JSON.stringify(configToApply));
        }
      }
    });
  }

  showSection(indexname: string) {
    const allSections = this.querySelectorAll('.column-right search-section');
    if (indexname === 'tous') {
      allSections.forEach((section) => {
        section.classList.remove('hidden');
      });
    } else {
      const selectedSection = this.querySelector(`.column-right search-section[data-indexname="${indexname}"]`);
      if (selectedSection) {
        allSections.forEach((section) => section.classList.add('hidden'));
        selectedSection.classList.remove('hidden');
      }
    }
  }

  typologieOnClick(index: string) {
    const liItems = this.querySelectorAll('.typologie-container li');
    liItems.forEach((liItem) => {
      const { indexname } = (liItem as HTMLLIElement).dataset;
      liItem.classList.toggle('active', indexname === index);
    });

    this.showSection(index);
    this.setAttribute('data-active-index', index);
    this.activeSection = index;

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }

  updateTotalPerSection() {
    const getLiItem = (section: ISection, indexActive: string) => {
      const li = document.createElement('li');
      li.setAttribute('data-indexname', section.indexname);
      li.innerHTML = `<span class="name">${section.collectionName}</span><span class="nb-hits">${section.nbHits}</span>`;
      if (section.indexname === indexActive) li.classList.add('active');
      return li;
    };

    const containerEl = this.querySelector('.search-result-menus .typologie-container');
    const ol = document.createElement('ol');
    let isShow = false;

    if (this.sections) {
      ol.appendChild(getLiItem({
        indexname: 'tous',
        collectionName: 'Tous',
        nbHits: this.getTotalRecords(),
        loaded: true,
      }, this.activeSection));

      for (const section of this.sections) {
        if (section.nbHits > 0) {
          ol.appendChild(getLiItem(section, this.activeSection));
          isShow = true;
        }
      }
    }

    if (containerEl) {
      containerEl.innerHTML = ol.outerHTML;
      if (isShow) this.querySelector('.search-result-menus')?.classList.add('show');
    }
  }

  attachEventToTypologie() {
    setTimeout(() => {
      const liItems = this.querySelectorAll('.typologie-container li');
      liItems.forEach(liItem => {
        const theLi = (liItem as HTMLLIElement);
        theLi.addEventListener('click', () => {
          this.typologieOnClick(`${theLi.dataset?.indexname}`);
        });
      });
    }, 200);
  }

  refinementClickHandler(e: Event) {
    const item = (<HTMLElement>e.target)?.closest('label.ais-RefinementList-label');
    if (item) {
      const { indexName } = (<HTMLLabelElement>item).dataset;
      if (indexName) this.typologieOnClick(indexName);
    }
  }

  async sendAnalyticsEvent() {
    const isLoaded = this.sections?.every((section) => section.loaded);

    if (isLoaded && this.query) {
      const total = this.getTotalRecords();
      if (total) {
        if (!this.isQuerySent) {
          if (this.typesenseClient) {
            this.isQuerySent = true;
            // Send
            await this.typesenseClient.analytics.events().create({
              name: 'global_popular_queries_rule',
              type: 'search',
              data: {
                q:  this.query,
                user_id: this.searchEventUserId,
              },
            });
          }
        }
      }
    }
  }

  init() {
    const {
      indexList,
      query,
      searchEventUserId,
    } = this.dataset;
    this.sections = this.getSections(indexList);
    this.query = query ?? '';
    this.searchEventUserId = searchEventUserId ?? '';
    this.totalResultsContainerEl = this.querySelector('.content-header .total-results') as HTMLDivElement;

    this.addEventListener(ON_RENDER_INSTANTSEARCH, (evt) => {
      const detail = (evt as CustomEvent).detail;

      this.sections?.forEach((section) => {
        if (section.indexname === detail.indexname) {
          section.loaded = true;
        }
      });

      this.updateTotalTitle({
        indexname: detail.indexname,
        results: detail.uiState.hits?.results,
      });
      this.updateTotalPerSection();
      this.attachEventToTypologie();
      this.sendAnalyticsEvent();
      console.log('--> ', detail.indexname);
    });

    document.addEventListener('click', this.refinementClickHandler.bind(this));
  }

  connectedCallback() {
    setTimeout(() => {
      this.init();
      this.initSearchInstance();
    });
  }
}
